import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { Box, Typography } from '../../../../1-primative';
import {
  Button, Dialog, DialogContent, DialogFooter, DialogTitle, IconButton,
} from '../../../../2-component';
import { CopyId } from '../../../../3-pattern';
import { formatMoneyValue } from '../../../../../util';
import { usePermissions } from '../../../../../providers/userContextProvider';
import { RECONCILE_CUSTODIAN_ACTIVITY, FORCE_RECONCILE_CUSTODIAN_ACTIVITY } from '../custodianActivities.queries';
import { CustodianActivity, CustodianActivityStates } from '../../../../../interfaces';

export const CustodianActivityModal = ({
  activity, setOpen, open, onTransition, options,
}: { activity: CustodianActivity, setOpen: (val: boolean) => void, open: boolean, onTransition: () => void, options?: any }) => {
  const { t } = useTranslation(['components', 'shared']);
  const { permissions } = usePermissions();

  const onReconcile = () => {
    onTransition();
    setOpen(false);
  };

  const [reconcileActivity] = useMutation(RECONCILE_CUSTODIAN_ACTIVITY, { onCompleted: onReconcile });
  const [forceReconcileActivity] = useMutation(FORCE_RECONCILE_CUSTODIAN_ACTIVITY, { onCompleted: onReconcile });

  const hasReconcilePermission = permissions.includes('write:custodian_activity_basic');
  const canReconcile = options?.enableReconcileAction && hasReconcilePermission;
  const canForceReconcile = options?.enableForceReconcileAction && hasReconcilePermission;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          {t('components:custodianActivities.modal.activityDetails')}
          <Box display='flex' justifyContent='end' alignItems='center'>
            <CopyId id={activity.id} />
            <Box ml={1} />
            <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant='bodyMedium' weight='bold'>{activity.transactionDesc}</Typography>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' flexDirection='column'>
            <Typography variant='displayMedium'>{formatMoneyValue(activity.amountValueCents)}</Typography>
            <Typography variant='bodySmall' colorVariant='variant'>{activity.transactionType}</Typography>
          </Box>
          {activity.symbol && (
            <Box display='flex' flexDirection='column' alignItems='flex-end'>
              <Box display='flex' justifyContent='space-between' minWidth='170px'>
              <Typography variant='bodySmall'>{t('components:trades.symbol')}</Typography>
              <Typography variant='bodySmall' weight='bold'>{activity.symbol}</Typography>
            </Box>
            <Box display='flex' justifyContent='space-between' minWidth='170px'>
              <Typography variant='bodySmall'>{t('components:trades.quantity')}</Typography>
              <Typography variant='bodySmall' weight='bold'>{activity.quantity}</Typography>
            </Box>
            <Box display='flex' justifyContent='space-between' minWidth='170px'>
              <Typography variant='bodySmall'>{t('components:trades.price')}</Typography>
              <Typography variant='bodySmall' weight='bold'>{formatMoneyValue(activity.priceCents)}</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      {activity.state === CustodianActivityStates.CREATED && (canReconcile || canForceReconcile) && (
        <DialogFooter>
          <Box display='flex' justifyContent='end' alignItems='center' width='100%'>
            <Box display='flex'>
              {canReconcile && (
                <Button label={t('components:custodianActivities.modal.reconcileButton')} onClick={() => reconcileActivity({
                  variables: { custodianActivityId: activity.id },
                })} sx={{ ml: 1 }} />
              )}
              {canForceReconcile && (
                <Button label={t('components:custodianActivities.modal.forceReconcileButton')} onClick={() => forceReconcileActivity({
                  variables: { custodianActivityId: activity.id },
                })} sx={{ ml: 1 }} />
              )}
            </Box>
          </Box>
        </DialogFooter>
      )}
    </Dialog>
  );
};
